import type { BusinessCenterClientDetail, BusinessCenterProjectDetail } from "../../lib/types";
import type {
  BusinessDiscoveryCallRecord,
  BusinessFollowUpRecord,
  BusinessLeadRecord,
  BusinessOpportunityRecord,
  BusinessSalesWorkspacePayload,
} from "./TruevanceSalesCenterData";
import type { TruevanceStore, TruevanceTimelineEvent } from "./TruevanceConnectData";

export interface TruevanceActivityItem {
  id: string;
  source: string;
  title: string;
  detail: string;
  date: string;
  relatedLink?: string;
  kind: "lead" | "discovery-call" | "opportunity" | "follow-up" | "assessment" | "recommendation" | "roadmap" | "project" | "client" | "note";
}

type TimelineEventLike = {
  id: string;
  title: string;
  detail: string;
  date?: string;
  createdAt?: string;
  relatedLink?: string;
};

export function appendNoteText(existing: string | undefined, heading: string, detail: string) {
  const nextLines = [
    existing?.trim() || "",
    `${new Date().toISOString().slice(0, 10)} · ${heading}: ${detail}`.trim(),
  ].filter(Boolean);
  return nextLines.join("\n\n");
}

export function collectTruevanceActivityFeed(input: {
  salesWorkspace?: BusinessSalesWorkspacePayload | null;
  truevanceStore?: TruevanceStore | null;
  clientDetail?: BusinessCenterClientDetail | null;
  projectDetail?: BusinessCenterProjectDetail | null;
}): TruevanceActivityItem[] {
  const items: TruevanceActivityItem[] = [];
  const pushTimeline = (source: string, kind: TruevanceActivityItem["kind"], events?: Array<TimelineEventLike> | Array<TruevanceTimelineEvent> | null, relatedLink?: string) => {
    (events || []).forEach((event) => {
      const timelineEvent = event as TimelineEventLike;
      items.push({
        id: event.id,
        source,
        kind,
        title: event.title,
        detail: event.detail,
        date: timelineEvent.date || timelineEvent.createdAt || new Date().toISOString(),
        relatedLink: timelineEvent.relatedLink || relatedLink,
      });
    });
  };

  const salesWorkspace = input.salesWorkspace;
  if (salesWorkspace) {
    salesWorkspace.leads.forEach((lead: BusinessLeadRecord) => pushTimeline("Lead", "lead", lead.timelineEvents, `/admin/truevance-sales-center`));
    salesWorkspace.discoveryCalls.forEach((call: BusinessDiscoveryCallRecord) =>
      pushTimeline("Discovery Call", "discovery-call", call.timelineEvents, `/admin/truevance-sales-center`),
    );
    salesWorkspace.opportunities.forEach((opportunity: BusinessOpportunityRecord) =>
      pushTimeline("Opportunity", "opportunity", opportunity.timelineEvents, `/admin/truevance-sales-center`),
    );
    salesWorkspace.followUps.forEach((followUp: BusinessFollowUpRecord) => {
      items.push({
        id: followUp.id,
        source: "Follow-up",
        kind: "follow-up",
        title: `${followUp.followUpNumber} · ${followUp.status}`,
        detail: followUp.notes || `${followUp.priority} priority follow-up due ${followUp.dueDate}`,
        date: followUp.completedAt || followUp.createdAt,
        relatedLink: `/admin/truevance-sales-center`,
      });
    });
  }

  if (input.truevanceStore) {
    input.truevanceStore.opportunities.forEach((record) => pushTimeline("Consulting Opportunity", "opportunity", record.timelineEvents, "/admin/truevance-opportunities"));
    input.truevanceStore.assessments.forEach((record) => pushTimeline("Assessment", "assessment", record.timelineEvents, "/admin/truevance-assessments"));
    input.truevanceStore.recommendations.forEach((record) => pushTimeline("Recommendation", "recommendation", record.timelineEvents, "/admin/truevance-recommendations"));
    input.truevanceStore.roadmaps.forEach((record) => pushTimeline("Roadmap", "roadmap", record.timelineEvents, "/admin/implementation-roadmaps"));
    input.truevanceStore.projects.forEach((record) => pushTimeline("Project", "project", record.timelineEvents, "/admin/truevance-projects"));
  }

  if (input.clientDetail) {
    input.clientDetail.timeline.forEach((event) =>
      items.push({
        id: event.id,
        source: "Client",
        kind: "client",
        title: event.title,
        detail: event.description,
        date: event.createdAt,
        relatedLink: event.relatedUrl || "/admin/truevance-client-center",
      }),
    );
  }

  if (input.projectDetail) {
    input.projectDetail.timeline.forEach((event) =>
      items.push({
        id: event.id,
        source: "Project",
        kind: "project",
        title: event.title,
        detail: event.detail,
        date: event.createdAt,
        relatedLink: event.relatedLink || `/admin/truevance-project-center/${input.projectDetail?.project.id}`,
      }),
    );
  }

  return items.sort((left, right) => new Date(right.date).getTime() - new Date(left.date).getTime());
}
